/**
 * Cutting stock and the leftover from cutting a board to a job's size.
 *
 * Cutting stock is sheets already cut to a size, kept at cutting: a lot coded
 * CUT01, CUT02 ... (not a SKU number), never bought again, and gone from the
 * stock list once it has no sheets left (its movements stay in the reports).
 * When a full sheet is cut to a job's cut size, the strip left over is worth
 * keeping as cutting stock if both its sides are at least MIN_STRIP_IN.
 */
export const isCutSku = (sku: string | null | undefined) => (sku ?? '').trim().toUpperCase().startsWith('CUT');

/** A leftover narrower than this (inches) is not suggested. */
export const MIN_STRIP_IN = 10;

const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * A job card's cut size ("711 x 800" or legacy "28 x 31.5") in inches,
 * longer side first.  A side over 60 is in mm - no sheet is 60 mm or less.
 */
export function parseCutSize(text: string | null | undefined): { l: number; w: number } | null {
  const nums = (text ?? '').split(/\s*[xX×*]\s*/).map((p) => parseFloat(p));
  if (nums.length !== 2 || !nums.every((n) => Number.isFinite(n) && n > 0)) return null;
  const [a, b] = nums.map((n) => r2(n > 60 ? n / 25.4 : n)) as [number, number];
  return { l: Math.max(a, b), w: Math.min(a, b) };
}

export interface Strip {
  /** Longer side first, inches. */
  l: number; w: number;
  /** How many of this strip one sheet gives. */
  perSheet: number;
}

export interface CutPlan {
  /** Pieces of the cut size from one sheet: `across` along the sheet's length x `down`. */
  pieces: number; across: number; down: number;
  /** The cut size as laid: `a` along the sheet's length. */
  a: number; b: number;
  /** Leftover strips from one sheet worth keeping, biggest first. */
  strips: Strip[];
  /** Square inches per sheet cut off and not kept. */
  wasteSqIn: number;
}

/**
 * How a sheet (L x W in) is best cut to a cut size: the most pieces, then
 * the most leftover worth keeping.  The cutter's first cut goes across the
 * sheet's length or its width; each gives two strips.
 */
export function cutPlan(sheetL: number, sheetW: number, cutL: number, cutW: number): CutPlan | null {
  if (!(sheetL > 0 && sheetW > 0 && cutL > 0 && cutW > 0)) return null;
  const eps = 0.005;
  let best: (CutPlan & { keep: number; top: number }) | null = null;
  for (const [a, b] of [[cutL, cutW], [cutW, cutL]] as const) {
    const across = Math.floor((sheetL + eps) / a);
    const down = Math.floor((sheetW + eps) / b);
    const pieces = across * down;
    if (!pieces) continue;
    const usedL = r2(across * a);
    const usedW = r2(down * b);
    const restL = r2(sheetL - usedL);
    const restW = r2(sheetW - usedW);
    const orders: [number, number][][] = [
      [[restL, sheetW], [usedL, restW]], // first cut across the length
      [[sheetL, restW], [restL, usedW]], // first cut along the width
    ];
    for (const order of orders) {
      const strips: Strip[] = [];
      let keep = 0;
      for (const [x, y] of order) {
        if (Math.min(x, y) < MIN_STRIP_IN) continue;
        const s = { l: r2(Math.max(x, y)), w: r2(Math.min(x, y)), perSheet: 1 };
        const same = strips.find((t) => t.l === s.l && t.w === s.w);
        if (same) same.perSheet += 1; else strips.push(s);
        keep += x * y;
      }
      strips.sort((p, q) => q.l * q.w - p.l * p.w);
      const top = strips[0] ? strips[0].l * strips[0].w : 0;
      const wasteSqIn = r2(sheetL * sheetW - pieces * a * b - keep);
      const better = !best
        || pieces > best.pieces
        || (pieces === best.pieces && (keep > best.keep + 0.001 || (Math.abs(keep - best.keep) <= 0.001 && top > best.top + 0.001)));
      if (better) best = { pieces, across, down, a, b, strips, wasteSqIn, keep, top };
    }
  }
  if (!best) return null;
  const { keep: _k, top: _t, ...plan } = best;
  return plan;
}
